import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  Button,
  Progress,
  VStack,
  Stack,
  IconButton,
  HStack,
  useColorModeValue,
  Collapse,
  Center,
  Spinner,
} from '@chakra-ui/react'
import { CaretDown, CaretUp, Timer } from '@phosphor-icons/react'
import { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import CubeAnimator from '../components/CubeAnimator'
import OLLCaseVisualizer from '../components/OLLCaseVisualizer'
import { useAuth } from '../contexts/AuthContext'
import { doc, updateDoc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase/config'
import { PageMeta } from '../components/PageMeta'

interface Algorithm {
  id: string
  name: string
  notation: string
  description: string
  status: 'unknown' | 'learning' | 'mastered'
  category: string
}

const initialOLLAlgorithms: Algorithm[] = [
  // All Corners Oriented (Cross Cases)
  {
    id: 'OLL21',
    name: 'H Cross',
    notation: 'R U2 R\' U\' R U R\' U\' R U\' R\'',
    description: 'All corners oriented, H pattern on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },
  {
    id: 'OLL22',
    name: 'Pi Cross',
    notation: 'R U2 R2 U\' R2 U\' R2 U2 R',
    description: 'All corners oriented, Pi pattern on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },
  {
    id: 'OLL23',
    name: 'U Cross',
    notation: 'R2 D R\' U2 R D\' R\' U2 R\'',
    description: 'All corners oriented, U pattern on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },
  {
    id: 'OLL24',
    name: 'T Cross',
    notation: 'r U R\' U\' r\' F R F\'',
    description: 'All corners oriented, T pattern on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },
  {
    id: 'OLL25',
    name: 'L Cross',
    notation: 'F\' r U R\' U\' r\' F R',
    description: 'All corners oriented, L pattern on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },
  {
    id: 'OLL26',
    name: 'S Cross',
    notation: 'R U2 R\' U\' R U\' R\'',
    description: 'All corners oriented, S pattern on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },
  {
    id: 'OLL27',
    name: 'Cross',
    notation: 'R U R\' U R U2 R\'',
    description: 'All corners oriented, full cross on top',
    status: 'unknown',
    category: 'All Corners Oriented'
  },

  // T Shapes
  {
    id: 'OLL1',
    name: 'T Shape 1',
    notation: 'R U2 R2 F R F\' U2 R\' F R F\'',
    description: 'T shape with headlights on front',
    status: 'unknown',
    category: 'T Shapes'
  },
  {
    id: 'OLL2',
    name: 'T Shape 2',
    notation: 'F R U R\' U\' F\' f R U R\' U\' f\'',
    description: 'T shape with no headlights',
    status: 'unknown',
    category: 'T Shapes'
  },

  // Square Shapes
  {
    id: 'OLL3',
    name: 'Square Shape 1',
    notation: 'f R U R\' U\' f\'',
    description: 'Square shape with headlights on front',
    status: 'unknown',
    category: 'Square Shapes'
  },
  {
    id: 'OLL4',
    name: 'Square Shape 2',
    notation: 'f R U R\' U\' f\' U\' F R U R\' U\' F\'',
    description: 'Square shape with no headlights',
    status: 'unknown',
    category: 'Square Shapes'
  },

  // C Shapes
  {
    id: 'OLL5',
    name: 'C Shape 1',
    notation: 'r\' U2 R U R\' U r',
    description: 'C shape with bar on right',
    status: 'unknown',
    category: 'C Shapes'
  },
  {
    id: 'OLL6',
    name: 'C Shape 2',
    notation: 'r U2 R\' U\' R U\' r\'',
    description: 'C shape with bar on left',
    status: 'unknown',
    category: 'C Shapes'
  },

  // Small L Shapes
  {
    id: 'OLL7',
    name: 'Small L 1',
    notation: 'F R U R\' U\' F\'',
    description: 'Small L shape with bar on right',
    status: 'unknown',
    category: 'Small L Shapes'
  },
  {
    id: 'OLL8',
    name: 'Small L 2',
    notation: 'R U R\' U\' R\' F R F\'',
    description: 'Small L shape with bar on left',
    status: 'unknown',
    category: 'Small L Shapes'
  },

  // Fish Shapes
  {
    id: 'OLL9',
    name: 'Fish Shape 1',
    notation: 'R U R\' U\' R\' F R2 U R\' U\' F\'',
    description: 'Fish shape pointing right',
    status: 'unknown',
    category: 'Fish Shapes'
  },
  {
    id: 'OLL10',
    name: 'Fish Shape 2',
    notation: 'R U R\' U R\' F R F\' R U2 R\'',
    description: 'Fish shape pointing left',
    status: 'unknown',
    category: 'Fish Shapes'
  },

  // P Shapes
  {
    id: 'OLL11',
    name: 'P Shape 1',
    notation: 'f R U R\' U\' f\' U F R U R\' U\' F\'',
    description: 'P shape with bar on right',
    status: 'unknown',
    category: 'P Shapes'
  },
  {
    id: 'OLL12',
    name: 'P Shape 2',
    notation: 'F R U R\' U\' F\' U f R U R\' U\' f\'',
    description: 'P shape with bar on left',
    status: 'unknown',
    category: 'P Shapes'
  },

  // W Shapes
  {
    id: 'OLL13',
    name: 'W Shape',
    notation: 'R U2 R2 F R F\' U2 M\' U R U\' r\'',
    description: 'W shape pattern',
    status: 'unknown',
    category: 'W Shapes'
  },
  {
    id: 'OLL14',
    name: 'Lightning Bolt',
    notation: 'R\' F R U R\' F\' R F U\' F\'',
    description: 'Lightning bolt pattern',
    status: 'unknown',
    category: 'W Shapes'
  },

  // Knight Move Shapes
  {
    id: 'OLL15',
    name: 'Knight Move 1',
    notation: 'r\' U\' r R\' U\' R U r\' U r',
    description: 'Knight move shape pointing right',
    status: 'unknown',
    category: 'Knight Move Shapes'
  },
  {
    id: 'OLL16',
    name: 'Knight Move 2',
    notation: 'r U r\' R U R\' U\' r U\' r\'',
    description: 'Knight move shape pointing left',
    status: 'unknown',
    category: 'Knight Move Shapes'
  },

  // Awkward Shapes
  {
    id: 'OLL17',
    name: 'Awkward 1',
    notation: 'R U R\' U R\' F R F\' U2 R\' F R F\'',
    description: 'Awkward shape with bar on right',
    status: 'unknown',
    category: 'Awkward Shapes'
  },
  {
    id: 'OLL18',
    name: 'Awkward 2',
    notation: 'r U R\' U R U2 r\'',
    description: 'Awkward shape with bar on left',
    status: 'unknown',
    category: 'Awkward Shapes'
  },
  {
    id: 'OLL19',
    name: 'Awkward 3',
    notation: 'M U R U R\' U\' M\' R\' F R F\'',
    description: 'Awkward shape with no bars',
    status: 'unknown',
    category: 'Awkward Shapes'
  },
  {
    id: 'OLL20',
    name: 'Awkward 4',
    notation: 'M U R U R\' U\' M2 U R U\' r\'',
    description: 'Awkward shape with dot',
    status: 'unknown',
    category: 'Awkward Shapes'
  },

  // Dot Cases
  {
    id: 'OLL28',
    name: 'Dot 1',
    notation: 'r U R\' U\' M U R U\' R\'',
    description: 'Dot case with no edges oriented',
    status: 'unknown',
    category: 'Dot Cases'
  },
  {
    id: 'OLL29',
    name: 'Dot 2',
    notation: 'M U R U R\' U\' R\' M\' R U\' R U\' R\'',
    description: 'Dot case with one edge oriented',
    status: 'unknown',
    category: 'Dot Cases'
  },
  {
    id: 'OLL30',
    name: 'Dot 3',
    notation: 'F U R U2 R\' U\' R U2 R\' U\' F\'',
    description: 'Dot case with two adjacent edges oriented',
    status: 'unknown',
    category: 'Dot Cases'
  },
  {
    id: 'OLL31',
    name: 'Dot 4',
    notation: 'R\' U\' F U R U\' R\' F\' R',
    description: 'Dot case with two opposite edges oriented',
    status: 'unknown',
    category: 'Dot Cases'
  },

  // L Shapes
  {
    id: 'OLL32',
    name: 'Big L 1',
    notation: 'R U B\' U\' R\' U R B R\'',
    description: 'Big L shape with bar on right',
    status: 'unknown',
    category: 'L Shapes'
  },
  {
    id: 'OLL33',
    name: 'Big L 2',
    notation: 'R U R\' U\' R\' F R F\'',
    description: 'Big L shape with bar on left',
    status: 'unknown',
    category: 'L Shapes'
  },

  // Line Cases
  {
    id: 'OLL34',
    name: 'Line 1',
    notation: 'R U R2 U\' R\' F R U R U\' F\'',
    description: 'Line case with bar on right',
    status: 'unknown',
    category: 'Line Cases'
  },
  {
    id: 'OLL35',
    name: 'Line 2',
    notation: 'R U2 R2 F R F\' R U2 R\'',
    description: 'Line case with bar on left',
    status: 'unknown',
    category: 'Line Cases'
  },

  // All Edges Oriented
  {
    id: 'OLL36',
    name: 'Sune',
    notation: 'R U R\' U R U2 R\'',
    description: 'All edges oriented, Sune case',
    status: 'unknown',
    category: 'All Edges Oriented'
  },
  {
    id: 'OLL37',
    name: 'Anti-Sune',
    notation: 'R U2 R\' U\' R U\' R\'',
    description: 'All edges oriented, Anti-Sune case',
    status: 'unknown',
    category: 'All Edges Oriented'
  }
]

const OLLMethod = () => {
  const { currentUser } = useAuth()
  const [algorithms, setAlgorithms] = useState<Algorithm[]>(initialOLLAlgorithms)
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string>('')
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})
  const [showLearningOnly, setShowLearningOnly] = useState(false)
  const [loading, setLoading] = useState(true)

  // Load saved progress when component mounts
  useEffect(() => {
    const loadProgress = async () => {
      if (!currentUser) {
        setLoading(false)
        return
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid)
        const userDoc = await getDoc(userRef)
        const userData = userDoc.data()

        if (userData?.progress?.oll?.algorithms) {
          const savedProgress = userData.progress.oll.algorithms
          const updatedAlgorithms = algorithms.map(algo => ({
            ...algo,
            status: savedProgress[algo.id] || 'unknown'
          }))
          setAlgorithms(updatedAlgorithms)
        }
      } catch (error) {
        console.error('Error loading OLL progress:', error)
      } finally {
        setLoading(false)
      }
    }

    loadProgress()
  }, [currentUser])

  // Color mode values
  const bgElevated = useColorModeValue('gray.50', 'keymash.bg.elevated')
  const bgSubtle = useColorModeValue('gray.100', 'keymash.bg.subtle')
  const textColor = useColorModeValue('gray.800', 'white')
  const borderColor = useColorModeValue('gray.200', 'keymash.border.subtle')
  const categoryColor = useColorModeValue('orange.500', 'orange.300')

  const updateAlgorithmStatus = async (
    id: string,
    newStatus: Algorithm['status']
  ) => {
    if (!currentUser) return

    const updatedAlgorithms = algorithms.map(algo => {
      if (algo.id === id) {
        // If clicking the same status again, revert to unknown
        const finalStatus = algo.status === newStatus ? 'unknown' : newStatus
        return { ...algo, status: finalStatus }
      }
      return algo
    })
    setAlgorithms(updatedAlgorithms)

    // Save progress to user profile
    const userRef = doc(db, 'users', currentUser.uid)
    const groupProgress = getGroupProgress(updatedAlgorithms)
    const overallProgress = getProgress(updatedAlgorithms)

    await updateDoc(userRef, {
      'progress.oll': {
        overall: overallProgress,
        groups: groupProgress,
        lastUpdated: new Date(),
        algorithms: updatedAlgorithms.reduce((acc, algo) => ({
          ...acc,
          [algo.id]: algo.status
        }), {})
      }
    })
  }

  const getProgress = (algorithms: Algorithm[]) => {
    const mastered = algorithms.filter(algo => algo.status === 'mastered').length
    return (mastered / algorithms.length) * 100
  }

  const getGroupProgress = (algorithms: Algorithm[]) => {
    const groups = algorithms.reduce((acc, algo) => {
      if (!acc[algo.category]) {
        acc[algo.category] = { total: 0, mastered: 0 }
      }
      acc[algo.category].total++
      if (algo.status === 'mastered') {
        acc[algo.category].mastered++
      }
      return acc
    }, {} as Record<string, { total: number; mastered: number }>)

    return Object.entries(groups).reduce((acc, [category, { total, mastered }]) => ({
      ...acc,
      [category]: (mastered / total) * 100
    }), {})
  }

  const toggleGroup = (category: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  // Group algorithms by category
  const groupedAlgorithms = algorithms.reduce((acc, algo) => {
    if (!acc[algo.category]) {
      acc[algo.category] = []
    }
    acc[algo.category].push(algo)
    return acc
  }, {} as Record<string, Algorithm[]>)

  // Filter algorithms if showLearningOnly is true
  const filteredGroupedAlgorithms = showLearningOnly
    ? Object.entries(groupedAlgorithms).reduce((acc, [category, algos]) => {
        const filteredAlgos = algos.filter(algo => algo.status === 'learning')
        if (filteredAlgos.length > 0) {
          acc[category] = filteredAlgos
        }
        return acc
      }, {} as Record<string, Algorithm[]>)
    : groupedAlgorithms

  return (
    <Box py={8} px={4}>
      <PageMeta title="oll.pageTitle" description="oll.pageDescription" />
      <VStack spacing={6} align="stretch" maxW="container.md" mx="auto">
        <Box>
          <Heading size="xl" mb={4}>OLL (Orientation of Last Layer)</Heading>
          <Text fontSize="lg" mb={8}>
            Learn the algorithms to orient all pieces in the last layer correctly.
          </Text>
        </Box>

        {loading ? (
          <Center py={8}>
            <Spinner size="xl" color="brand.500" />
          </Center>
        ) : (
          <>
            {selectedAlgorithm && (
              <Box mb={8} display="flex" justifyContent="center">
                <CubeAnimator
                  algorithm={selectedAlgorithm}
                  showCase={true}
                  speed={500}
                  showControls={true}
                  autoPlay={false}
                  size="280px"
                />
              </Box>
            )}

            <HStack justify="space-between" align="center" mb={4}>
              <Box>
                <Text mb={2}>Overall Progress:</Text>
                <Progress value={getProgress(algorithms)} colorScheme="green" width="200px" />
              </Box>
              <Button
                size="sm"
                colorScheme="blue"
                variant={showLearningOnly ? 'solid' : 'outline'}
                onClick={() => setShowLearningOnly(!showLearningOnly)}
              >
                {showLearningOnly ? 'Show All' : 'Show Learning Only'}
              </Button>
            </HStack>

            <VStack spacing={6} align="stretch">
              {Object.entries(filteredGroupedAlgorithms).map(([category, algos]) => {
                const groupProgress = getProgress(algos)
                const isCollapsed = collapsedGroups[category]

                return (
                  <Box key={category}>
                    <Card mb={isCollapsed ? 0 : 6}>
                      <CardBody p={4}>
                        <HStack justify="space-between" align="center" width="100%">
                          <HStack spacing={4}>
                            <IconButton
                              aria-label={isCollapsed ? "Expand group" : "Collapse group"}
                              icon={isCollapsed ? <CaretDown weight="bold" /> : <CaretUp weight="bold" />}
                              variant="ghost"
                              color={categoryColor}
                              onClick={() => toggleGroup(category)}
                            />
                            <VStack align="start" spacing={1}>
                              <Heading size="md" color={categoryColor}>
                                {category}
                              </Heading>
                              <Text fontSize="sm" color="text.muted">
                                {Math.round(groupProgress)}% complete
                              </Text>
                            </VStack>
                          </HStack>
                          <Progress 
                            value={groupProgress} 
                            colorScheme="green" 
                            width="100px"
                            size="sm"
                            borderRadius="full"
                          />
                        </HStack>
                      </CardBody>
                    </Card>

                    <Collapse in={!isCollapsed} animateOpacity>
                      <VStack spacing={6} align="stretch">
                        {algos.map((algo) => (
                          <Card key={algo.id}>
                            <CardBody>
                              <VStack spacing={4} align="stretch">
                                <Heading size="md" textAlign="center">
                                  {algo.name}
                                </Heading>
                                
                                <Box>
                                  <OLLCaseVisualizer 
                                    key={`${algo.id}-${!isCollapsed}`}
                                    ollCase={algo.id} 
                                    size={{ width: "100%", height: "150px" }}
                                  />
                                </Box>

                                <Box
                                  bg={bgElevated}
                                  p={4}
                                  borderRadius="lg"
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                  cursor="pointer"
                                  onClick={() => setSelectedAlgorithm(algo.notation)}
                                  _hover={{ 
                                    bg: bgSubtle,
                                    borderColor: 'keymash.border.default'
                                  }}
                                  transition="all 0.2s"
                                >
                                  <Text
                                    fontFamily="mono"
                                    fontSize={{ base: "xl", md: "2xl" }}
                                    color={textColor}
                                    textAlign="center"
                                    letterSpacing="wider"
                                    fontWeight="medium"
                                  >
                                    {algo.notation}
                                  </Text>
                                </Box>

                                <Text fontSize="sm" color="text.muted" textAlign="center">
                                  {algo.description}
                                </Text>

                                <Stack
                                  direction={{ base: 'column', sm: 'row' }}
                                  spacing={2}
                                  justify="center"
                                  mt={2}
                                >
                                  <Button
                                    size="sm"
                                    variant={algo.status === 'learning' ? 'solid' : 'outline'}
                                    colorScheme="yellow"
                                    onClick={() => updateAlgorithmStatus(algo.id, 'learning')}
                                    width={{ base: '100%', sm: 'auto' }}
                                  >
                                    Learning
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={algo.status === 'mastered' ? 'solid' : 'outline'}
                                    colorScheme="green"
                                    onClick={() => updateAlgorithmStatus(algo.id, 'mastered')}
                                    width={{ base: '100%', sm: 'auto' }}
                                  >
                                    Mastered
                                  </Button>
                                </Stack>
                              </VStack>
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    </Collapse>
                  </Box>
                )
              })}
            </VStack>
          </>
        )}
      </VStack>
    </Box>
  )
}

export default OLLMethod 